// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { AuditPropertyUpdateLabel } from '@/audit/components/audit-property-update-label';
import { AuditPropertyValue } from '@/audit/components/audit-property-value';
import { Stack, Typography } from '@mui/material';

/**
 * The line-item shape every order manifest shares. Outbound and inbound orders
 * differ only in which quantity tiers they carry, so the renderer takes the tiers
 * as data.
 */
interface OrderManifestEntry {
  item: {
    id: string;
    name: string;
    sku?: string | null;
  };
  requestedQuantity: number | null;
}

/** One quantity tier's label and its old and new values. */
interface OrderManifestTier {
  label: string;
  old: number | null;
  new: number | null;
}

interface Props<TEntry extends OrderManifestEntry> {
  name: string;
  oldValue: readonly TEntry[] | null | undefined;
  newValue: readonly TEntry[] | null | undefined;
  /**
   * Returns the tiers to compare for a line present on both sides, in the order
   * they should read.
   */
  tiers: (oldEntry: TEntry, newEntry: TEntry) => OrderManifestTier[];
}

interface ManifestUpdate<TEntry> {
  id: string;
  name: string;
  sku: string;
  kind: 'added' | 'removed' | 'updated';
  old: TEntry | null;
  new: TEntry | null;
}

/**
 * Formats an item heading as "name (sku)", dropping the parenthetical for
 * legacy items with no SKU.
 */
function itemHeading(name: string, sku: string | null | undefined): string {
  return sku ? `${name} (${sku})` : name;
}

/**
 * Renders an order's line-item manifest as an old→new diff. A line present on one
 * side only reads as added or removed and shows the requested quantity the order
 * authored; a line on both sides shows only the tiers that moved.
 */
export function AuditOrderManifestLabel<TEntry extends OrderManifestEntry>(
  props: Props<TEntry>
) {
  const oldEntries = props.oldValue ?? [];
  const newEntries = props.newValue ?? [];

  const updates: ManifestUpdate<TEntry>[] = [];

  oldEntries.forEach((oldEntry) => {
    const newEntry =
      newEntries.find((entry) => entry.item.id === oldEntry.item.id) ?? null;
    updates.push({
      id: oldEntry.item.id,
      name: oldEntry.item.name,
      sku: oldEntry.item.sku ?? '',
      kind: newEntry === null ? 'removed' : 'updated',
      old: oldEntry,
      new: newEntry,
    });
  });

  newEntries.forEach((newEntry) => {
    const seen = updates.some((update) => update.id === newEntry.item.id);
    if (!seen) {
      updates.push({
        id: newEntry.item.id,
        name: newEntry.item.name,
        sku: newEntry.item.sku ?? '',
        kind: 'added',
        old: null,
        new: newEntry,
      });
    }
  });

  const rendered = updates
    .map((update) => renderUpdate(update, props.tiers))
    .filter((node) => node !== null);

  if (rendered.length === 0) {
    return null;
  }

  return (
    <Stack>
      <Typography component="span" variant="caption" fontWeight="bold">
        {props.name}:
      </Typography>
      {rendered}
    </Stack>
  );
}

function renderUpdate<TEntry extends OrderManifestEntry>(
  update: ManifestUpdate<TEntry>,
  tiersFor: (oldEntry: TEntry, newEntry: TEntry) => OrderManifestTier[]
) {
  if (update.kind === 'added' && update.new !== null) {
    return <SingleSidedLine key={update.id} entry={update.new} verb="added" />;
  }

  if (update.kind === 'removed' && update.old !== null) {
    return (
      <SingleSidedLine key={update.id} entry={update.old} verb="removed" />
    );
  }

  if (update.old === null || update.new === null) {
    return null;
  }

  const changed = tiersFor(update.old, update.new).filter(
    (tier) => tier.old !== tier.new
  );
  if (changed.length === 0) {
    return null;
  }

  return (
    <Stack key={update.id}>
      <Typography variant="caption" marginLeft={2}>
        {itemHeading(update.name, update.sku)}{' '}
        <Typography
          component="span"
          variant="inherit"
          sx={{ textDecoration: 'underline' }}
        >
          updated
        </Typography>
      </Typography>
      {changed.map((tier) => (
        <Typography
          key={tier.label}
          variant="caption"
          component="div"
          marginLeft={4}
        >
          <AuditPropertyUpdateLabel
            name={tier.label}
            oldValue={tier.old}
            newValue={tier.new}
          />
        </Typography>
      ))}
    </Stack>
  );
}

function SingleSidedLine(props: {
  entry: OrderManifestEntry;
  verb: 'added' | 'removed';
}) {
  return (
    <Stack>
      <Typography variant="caption" marginLeft={2}>
        {itemHeading(props.entry.item.name, props.entry.item.sku)}{' '}
        <Typography
          component="span"
          variant="inherit"
          sx={{ textDecoration: 'underline' }}
        >
          {props.verb}
        </Typography>
      </Typography>
      <Typography variant="caption" marginLeft={4}>
        Requested:&nbsp;
        <AuditPropertyValue value={props.entry.requestedQuantity} />
      </Typography>
    </Stack>
  );
}
